/**
 * Auth 模块 API
 * 对应 auth-service (7089)
 */

import { unifiedApi } from '@/utils/unifiedApi';
import type {
  LoginParams,
  LoginMobileParams,
  LoginResultModel,
  CaptchaResult,
  QuickLoginResult,
  QRCodeGenerateResult,
  QRCodeCheckResult,
  RefreshTokenRequest,
  ChangePasswordRequest,
  SelectTenantParams,
  PortalPreferenceParams,
  PortalPreferenceResult,
  TenantThemeResult,
} from './types';

/**
 * 用户登录
 * 调用 auth-service: POST /api/v1/auth/login
 */
export const loginApi = (params: LoginParams | LoginMobileParams) => {
  return unifiedApi.service('auth').post<LoginResultModel>('/auth/login', params);
};

/**
 * 获取验证码
 * 调用 auth-service: GET /api/v1/auth/captcha
 */
export const getCaptcha = () => {
  return unifiedApi.service('auth').get<CaptchaResult>('/auth/captcha');
};

/**
 * 快速登录（仅开发环境）
 * 调用 business-service: POST /api/v1/auth/quick-login
 */
export const quickLoginApi = (username: string = 'belle_admin') => {
  return unifiedApi.post<QuickLoginResult>('/api/v1/auth/quick-login', { username });
};

/**
 * 生成扫码登录二维码
 * 调用 auth-service: POST /api/v1/auth/qrcode/generate
 */
export const qrcodeGenerate = () => {
  return unifiedApi.service('auth').post<QRCodeGenerateResult>('/auth/qrcode/generate');
};

/**
 * 轮询二维码扫描状态
 * 调用 auth-service: GET /api/v1/auth/qrcode/check?qrcode_id=xxx
 */
export const qrcodeCheck = (qrcodeId: string) => {
  return unifiedApi.service('auth').get<QRCodeCheckResult>('/auth/qrcode/check', {
    params: { qrcode_id: qrcodeId },
  });
};

/**
 * REQ-20260508-0001 #10173: 多租户用户选定租户后换取最终 token
 * 调用 auth-service: POST /api/v1/auth/select-tenant
 */
export const selectTenantApi = (params: SelectTenantParams) => {
  return unifiedApi.service('auth').post<LoginResultModel>('/auth/select-tenant', params);
};

/**
 * REQ-20260508-0001 #10174: 保存当前用户的门户偏好
 * 调用 auth-service: PUT /api/v1/users/me/portal-preference
 */
export const savePortalPreference = (params: PortalPreferenceParams) => {
  return unifiedApi.service('auth').put<PortalPreferenceResult>('/users/me/portal-preference', params);
};

/**
 * REQ-20260508-0001 #10175: 公开租户主题查询（登录前调用，无需 token）
 * 调用 auth-service: GET /api/v1/auth/tenant-theme?code=xxx
 *
 * 注: 命名 ByCode 是为了与 foundation 模块的 getPublicTenantTheme（按 tenant_id 查）区分。
 */
export const getPublicTenantThemeByCode = (code: string) => {
  return unifiedApi.service('auth').get<TenantThemeResult>('/auth/tenant-theme', {
    params: { code },
  });
};

/**
 * 用户登出
 */
export const logoutApi = () => {
  return unifiedApi.service('auth').get('/logout');
};

/**
 * 刷新Token
 */
export const refreshToken = (params: RefreshTokenRequest) => {
  return unifiedApi.service('auth').post<LoginResultModel>('/auth/refresh', params);
};

/**
 * 获取用户信息
 * 调用 foundation-service
 */
export const getUserInfo = () => {
  return unifiedApi.service('user').get('/users/self/info');
};

/**
 * 修改密码
 * 调用 foundation-service
 */
export const changePassword = (params: ChangePasswordRequest) => {
  return unifiedApi.service('user').post('/user/self/change-password', params);
};

/**
 * 忘记密码
 */
export const forgotPassword = (email: string) => {
  return unifiedApi.service('auth').post('/forget/password', { email });
};

/**
 * 重置密码
 */
export const resetPassword = (params: { token: string; password: string }) => {
  return unifiedApi.service('user').post('/user/reset-password/', params);
};
